import { SupabaseClient } from "@supabase/supabase-js";
import { v4 as uuidv4 } from "uuid";
import { createFriendlyName } from "../../conversionUtils";
import WorkFactory from "../workFactory";
import { BaseWork, Work } from "@/app/types/book";
import { parseCharacterRole } from "../../parserUtils";
import { isDemoWork, sanitizeDemoWork } from "@/app/lib/utils/demoUtils";
import { FEATURES } from "@/app/lib/config";

/**
 * Create a new work for the given user.
 *
 * @param client - Initialized Supabase client
 * @param userId - Owner of the work
 * @param title - Title of the work
 * @param author - Author name
 * @param work_type - Type of work being created
 * @param metadata - Arbitrary metadata saved with the work
 * @param remote_file_metadata - Metadata from the uploaded file
 * @param friendlyId - Optional pre-defined friendly ID
 * @returns The newly created work in JSON format
 */
export async function createWork(
  client: SupabaseClient,
  userId: string,
  title: string,
  author: string,
  work_type: string,
  metadata: any = {},
  remote_file_metadata: any = {},
  friendlyId?: string
): Promise<BaseWork> {
  const id = uuidv4();
  const friendly_id = friendlyId || createFriendlyName(title);

  const { data, error } = await client
    .from("works")
    .insert({
      id,
      friendly_id,
      title,
      author,
      work_type,
      user_id: userId,
      metadata,
      remote_file_metadata,
      is_public: true,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Failed to create work");
  }

  return WorkFactory.workToJSON(data);
}

/**
 * Update metadata for an existing work.
 *
 * @param client - Supabase client
 * @param workId - Identifier of the work to update
 * @param metaData - Partial metadata object to merge
 * @returns Updated work converted to JSON
 */
export async function updateWork(
  client: SupabaseClient,
  workId: string,
  metaData: Record<string, any>
): Promise<BaseWork> {
  const updateData: any = { updated_at: new Date() };

  if (metaData) {
    updateData["metadata"] = metaData;
  }

  const { data, error } = await client
    .from("works")
    .update(updateData)
    .eq("id", workId)
    .select("*")
    .maybeSingle();

  if (error) {
    throw new Error(`Error updating analysis: ${error.message}`);
  }

  if (!data) {
    throw new Error("Failed to update analysis");
  }

  return WorkFactory.workToJSON(data);
}

/**
 * Retrieve all works owned by the given user, including demo works.
 *
 * @param client - Supabase client
 * @param userId - User whose works should be returned
 * @returns Array of works sorted by creation date
 */
export async function getWorksForUser(
  client: SupabaseClient,
  userId: string
): Promise<BaseWork[]> {
  const { data, error } = await client
    .from("works")
    .select("*")
    .or(`user_id.eq.${userId},tags.ilike.%DEMO%`)
    .eq("is_public", true)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(`Error fetching works: ${error.message}`);
  }

  return (data || []).map((work) => WorkFactory.workToJSON(work));
}

/**
 * Find a work for the user by its title.
 *
 * The title is converted into a friendly ID which must match exactly.
 *
 * @param client - Supabase client
 * @param userId - ID of the work owner
 * @param title - Title string to search for
 * @returns Matching work or null if none found
 */
export async function getWorkByTitle(
  client: SupabaseClient,
  userId: string,
  title: string
): Promise<BaseWork | null> {
  if (!title) {
    throw new Error("Missing title");
  }

  const friendlyId = createFriendlyName(title);

  const { data, error } = await client
    .from("works")
    .select("*")
    .eq("user_id", userId)
    .eq("friendly_id", friendlyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(`Error fetching works: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  return WorkFactory.workToJSON(data[0]);
}

/**
 * Load a specific work by its UUID for a user.
 *
 * Demo works are also available to every user.
 *
 * @param client - Supabase client
 * @param userId - ID of the user requesting the work
 * @param workId - UUID of the work
 * @returns Matching work or null if not found
 */
export async function getWorkById(
  client: SupabaseClient,
  userId: string,
  workId: string
): Promise<BaseWork | null> {
  if (!workId) {
    throw new Error("Missing workId");
  }

  const { data, error } = await client
    .from("works")
    .select("*")
    .or(`user_id.eq.${userId},tags.ilike.%DEMO%,id.eq.${workId}`)
    .eq("id", workId);

  if (error) {
    throw new Error(`Error fetching work: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  return WorkFactory.workToJSON(data[0]);
}

/**
 * Retrieve a single work by its friendly ID.
 *
 * Character metadata is enriched with analysis results when available.
 *
 * @param client - Supabase client
 * @param friendlyId - Human readable ID of the work
 * @param userId - Requesting user ID
 * @returns The full work with analysis metadata or null if not found
 */
export async function getWorkByFriendlyId(
  client: SupabaseClient,
  friendlyId: string,
  userId: string
): Promise<Work | null> {
  if (!friendlyId) {
    throw new Error("Missing friendlyId");
  }

  const { data, error } = await client
    .from("works")
    .select("*")
    .or(`user_id.eq.${userId},friendly_id.eq.${friendlyId},tags.ilike.%DEMO%`)
    .eq("friendly_id", friendlyId)
    .eq("is_public", true)
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching work: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  const { data: analyses } = await client
    .from("work_analyses")
    .select("*")
    .eq("work_id", data.id)
    .like("analysis_type", "character-analysis-%");

  if (analyses && data.metadata?.characters && analyses.length > 0) {
    data.metadata.characters = data.metadata.characters.map((character: any) => {
      const characterAnalysis = analyses.find(
        (analysis) =>
          analysis.analysis_type === `character-analysis-${character.friendly_id}`
      );
      if (!characterAnalysis) {
        return character;
      }

      if (characterAnalysis?.analysis_json?.character_significance?.score) {
        character.significance_score =
          characterAnalysis.analysis_json.character_significance.score;
      }

      if (characterAnalysis?.analysis_json?.role) {
        character.role = parseCharacterRole(
          characterAnalysis.analysis_json.role
        );
      }

      return character;
    });
  }

  return data as Work;
}

/**
 * Retrieve a public demo work by its friendly ID without requiring authentication.
 * This function sanitizes sensitive data before returning the work.
 *
 * @param client - Supabase client
 * @param friendlyId - Human readable ID of the work
 * @returns Sanitized demo work or null if not found or not a demo
 */
export async function getPublicDemoWork(
  client: SupabaseClient,
  friendlyId: string
): Promise<Work | null> {
  if (!FEATURES.PUBLIC_DEMO_WORKS) {
    return null;
  }

  if (!friendlyId) {
    throw new Error("Missing friendlyId");
  }

  // Use a service account ID for retrieving the work
  const serviceUserId = "00000000-0000-0000-0000-000000000000";
  
  // Attempt to get the work using the service user ID
  const work = await getWorkByFriendlyId(client, friendlyId, serviceUserId);
  
  // Check if it's a valid demo work
  if (!work || !isDemoWork(work)) {
    console.warn(`[demo-access] Attempted access to non-demo work rejected: ${friendlyId}`);
    return null;
  }
  
  // Log successful demo work access for monitoring
  console.log(`[demo-access] Public demo work accessed: ${friendlyId}, title: "${work.title}", timestamp: ${new Date().toISOString()}`);
  
  // Sanitize and return the work
  return sanitizeDemoWork(work);
}

/**
 * Retrieve all public demo works available in the system.
 *
 * @param client - Supabase client
 * @returns Array of sanitized demo works
 */
export async function getPublicDemoWorks(
  client: SupabaseClient
): Promise<Work[]> {
  if (!FEATURES.PUBLIC_DEMO_WORKS) {
    return [];
  }

  const { data, error } = await client
    .from("works")
    .select("*")
    .or("tags.ilike.%DEMO%,tags.ilike.%SAMPLE%")
    .eq("is_public", true)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(`Error fetching demo works: ${error.message}`);
  }

  // Filter to ensure works match our demo criteria and sanitize them
  return (data || [])
    .filter(work => isDemoWork(work))
    .map(work => sanitizeDemoWork(work));
}

/**
 * Get the current demo book.
 * Returns the first available demo book from the system.
 * Note: Caching is handled at the WorkDb layer.
 *
 * @param client - Supabase client
 * @returns The current demo book or null if none available
 */
export async function getCurrentDemoBook(
  client: SupabaseClient
): Promise<Work | null> {
  const demoWorks = await getPublicDemoWorks(client);
  return demoWorks.length > 0 ? demoWorks[0] : null;
}

